import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { prop, join, length, propEq } from 'ramda'

import {
  setSelectedIdWithDrawer,
  navigatorIsFullscreen,
} from 'actions/navigator'

import strings from 'utils/strings'
import { findLogicCountConflicts } from 'utils/truck'
import { tooManyResources } from 'i18n/displayConstants/addFeatureToVehicle'
import { is43NAlignedWith42N } from 'utils/pinRestrictions'

import { Tooltip, Button, Popover } from 'components/patterns'
import HelpIcon from 'img/help-icon-tooltip.svg'
import { DYNAMIC } from 'constants/featureTypes'
import { OBSOLETE, TRUCK_SUBTYPE_43N } from 'utils/constants'
import {
  hasEligibleSnapshotUpdate as hasEligibleSnapshotUpdateHelper,
  getModuleCapacity,
  hasNewerEligibleRevision,
  hasPendingUpdate as hasPendingUpdateHelper,
} from '../updateEligibility'
import { InfoIcon } from '../FeaturesTable.style'

const MoreInfo = ({
  record,
  moduleCounts,
  isEditable,
  isSummary,
  isCollectionEditMode,
  vehicleSubType,
  vehicle35mCode,
  buildDate,
  vehicleECUs,
}) => {
  const { isCompatible } = record
  const isDynamicFeature = propEq('type', DYNAMIC, record) && !isEditable

  // Bug #505985: these come from the shared updateEligibility helpers so that the
  // condition deciding whether this cell renders anything is the same one
  // FeaturesTable uses to decide whether to insert the column at all. They used to
  // be defined inline here and approximated over there, which let the column
  // appear with nothing in it.
  //
  // No module-authorization check any more: the remaining update path (the section
  // header's "Update Feature Versions" button) is not authorization-gated, so
  // requiring rights here left users with an enabled bulk-update action and no
  // warning. See hasPendingUpdate for the full reasoning.
  const hasNewerRevision = hasNewerEligibleRevision(record)
  const hasEligibleSnapshotUpdate = hasEligibleSnapshotUpdateHelper(record)
  const hasPendingUpdate = hasPendingUpdateHelper(record)

  const getMessage = (r) => {
    if (hasEligibleSnapshotUpdate || hasNewerRevision)
      return strings.newer_feature_version_available

    if (isCompatible === false)
      return length(prop('incompatibilityReason', r))
        ? join(' ', prop('incompatibilityReason', r))
        : strings.feature_incompatible

    if (isDynamicFeature) {
      return strings.feature_collections_dynamic_feature_tooltip_text
    }

    return null
  }

  const dispatch = useDispatch()
  const handleDrawer = () => {
    dispatch(navigatorIsFullscreen(true))
    dispatch(setSelectedIdWithDrawer(true))
  }

  const { isOverQueryCount, isOverTimerCount } = findLogicCountConflicts(
    record,
    moduleCounts,
    getModuleCapacity(record, vehicleECUs)
  )

  const resourcesError =
    isEditable &&
    record.isMarkedForDeletion &&
    tooManyResources(
      record,
      isOverQueryCount,
      isOverTimerCount,
      getModuleCapacity(record, vehicleECUs)
    )

  // Bug #505985: edit mode flags pending updates here again — the pre-PBI-233301
  // behaviour — now that the orange per-row icon in Add/Remove Features is gone.
  // The previous resource-limit gate on this tooltip only existed to cover the
  // case where no OverflowMenu was rendered; a pending update now always warns,
  // which subsumes it.
  //
  // tooltip appears when feature is not latest compared to the diagnostic version available,
  // has incompatible input/outputs, or has a newer revision available.
  //
  // The isEditable split is kept deliberately. Edit mode tests `isCompatible ===
  // false` while read mode tests `!isCompatible`, and those differ when the field
  // is absent: collapsing them into one `!isCompatible` would make every feature
  // with an undefined isCompatible sprout a triangle in Add/Remove Features.
  const showNewVersionTooltip = isEditable
    ? hasPendingUpdate || isCompatible === false
    : hasPendingUpdate || !isCompatible

  const getNewVersionToolTip = isEditable ? (
    <>{getMessage(record)}</>
  ) : (
    <>
      <span>{getMessage(record)}</span>
      {isCompatible && !isSummary && (
        <Button onClick={handleDrawer}>{strings.edit_features}</Button>
      )}
    </>
  )

  const newVersionAvailable = () => {
    if (showNewVersionTooltip) {
      return getNewVersionToolTip
    }
    return null
  }

  const dynamicFeatureText =
    isDynamicFeature && strings.dynamic_feature_tooltip_text

  const isObsolete = propEq('status', OBSOLETE, record)

  const obsoleteText =
    isObsolete &&
    (isCollectionEditMode
      ? strings.obsolete_feature_collection_edit_tooltip ||
        'Only Features with status "Active" may be added to a collection. CHEC recommends that you replace or remove "Obsolete" Features.'
      : strings.obsolete_feature_alert_tooltip)

  const vpdmText =
    record.isFeatureVpdmCompatible &&
    (vehicleSubType === TRUCK_SUBTYPE_43N &&
    !is43NAlignedWith42N(vehicle35mCode, buildDate)
      ? strings.add_feature_features_table_20a_output
      : strings.add_feature_features_table_2a_output)

  const text =
    obsoleteText ||
    resourcesError ||
    vpdmText ||
    newVersionAvailable() ||
    dynamicFeatureText
  if (!text) return <></>

  const Component =
    isEditable || isSummary || isDynamicFeature || isObsolete
      ? Tooltip
      : Popover

  const componentAdditionalProps =
    isEditable || isSummary || isDynamicFeature || isObsolete
      ? {
          overlayClassName: 'balanced-tooltip',
        }
      : {}

  // Show the help icon only when the dynamic-feature message is actually the
  // winning tooltip text. Any higher-priority warning (VPDM, obsolete,
  // incompatibility, resource limit) keeps the yellow warning triangle.
  const showHelpIcon = text === dynamicFeatureText

  return (
    <Component placement="right" title={text} {...componentAdditionalProps}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          width: 20,
          height: 18,
        }}
      >
        {showHelpIcon ? (
          <InfoIcon src={HelpIcon} />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={20}
            height={18}
            fill="none"
          >
            <path
              fill="#EFB115"
              fillRule="evenodd"
              d="M19.71 14.82 11.84 1.06a2.09 2.09 0 0 0-3.63 0L.31 14.82A2.12 2.12 0 0 0 2.1 18h15.8a2.12 2.12 0 0 0 1.81-3.18zM11 14.5H9v-2h2v2zm0-5a1 1 0 0 1-2 0v-2a1 1 0 0 1 2 0v2z"
              clipRule="evenodd"
            />
            <path
              fill="#07070F"
              d="M11 12.5H9v2h2v-2zm0-3v-2a1 1 0 0 0-2 0v2a1 1 0 1 0 2 0z"
            />
          </svg>
        )}
      </span>
    </Component>
  )
}

MoreInfo.propTypes = {
  isEditable: PropTypes.bool,
  record: PropTypes.shape({
    id: PropTypes.string,
    latestOnVehicle: PropTypes.bool,
    moduleName: PropTypes.string,
    moduleType: PropTypes.string,
    latestSnapshot: PropTypes.object,
    isMarkedForDeletion: PropTypes.bool,
    isCompatible: PropTypes.bool,
    isFeatureLatestRevision: PropTypes.bool,
    isFeatureVpdmCompatible: PropTypes.bool,
    revision: PropTypes.string,
    revisions: PropTypes.array,
  }),
  moduleCounts: PropTypes.object,
  isSummary: PropTypes.bool,
  isCollectionEditMode: PropTypes.bool,
  features: PropTypes.array,
  vehicleECUs: PropTypes.array,
  vehicleSubType: PropTypes.string,
  vehicle35mCode: PropTypes.string,
  buildDate: PropTypes.string,
}

export default MoreInfo
